import React, { useEffect } from 'react';
import * as api from 'config/api';
import {
  PortfolioLoading,
  PortfolioRatioStuffingProps,
  PortfolioRatioStuffingTabProps,
  TypeKey,
  getRatio_,
  rawDataList,
} from './PortfolioRatioStuffing';
import { PortfolioRatioStuffingInternal } from './PortfolioRatioStuffingInternal';
import { isHiddenProductTypes } from '../PortfolioStuffing/asset/PortfolioStuffingAsset';
import { useQuery } from 'react-query';

const convertToAccount = (res: api.GetAccountClassificationResult) => {
  return {
    TotalAsset: {
      general: res.TotalAsset.General,
      identification: res.TotalAsset.Identification,
      nisa: res.TotalAsset.Nisa,
      definedContributionPension: res.TotalAsset.DefinedContributionPension,
      other: res.TotalAsset.Other,
      positions: res.TotalAsset.Positions.map(position => {
        return {
          account: position.Account,
          accountType: position.AccountType,
          brokerID: position.BrokerID,
          cashMargin: position.CashMargin,
          currentAmount: position.CurrentAmount,
          currentAmountUSD: position.CurrentAmountUSD,
          price: position.Price,
          productType: position.ProductType,
          profitOrLoss: position.ProfitOrLoss,
          profitOrLossUSD: position.ProfitOrLossUSD,
          qty: position.Qty,
          side: position.Side,
          symbol: position.Symbol,
          symbolName: position.SymbolName,
        };
      }),
    },
    GroupAssets: res.GroupAssets.map(groupAsset => {
      return {
        ID: groupAsset.ID,
        name: groupAsset.Name,
        general: groupAsset.General,
        identification: groupAsset.Identification,
        nisa: groupAsset.Nisa,
        definedContributionPension: groupAsset.DefinedContributionPension,
        other: groupAsset.Other,
        positions: (groupAsset.Positions ?? []).map(position => {
          return {
            account: position.Account,
            accountType: position.AccountType,
            brokerID: position.BrokerID,
            cashMargin: position.CashMargin,
            currentAmount: position.CurrentAmount,
            currentAmountUSD: position.CurrentAmountUSD,
            price: position.Price,
            productType: position.ProductType,
            profitOrLoss: position.ProfitOrLoss,
            profitOrLossUSD: position.ProfitOrLossUSD,
            qty: position.Qty,
            side: position.Side,
            symbol: position.Symbol,
            symbolName: position.SymbolName,
          };
        }),
      };
    }),
  };
};

type AccountRowDataList = {
  general: number | null;
  identification: number | null;
  nisa: number | null;
  definedContributionPension: number | null;
  other: number | null;
  positions: AccountPositionItemsProps[];
};

const getAccountRowDataList = (data: AccountRowDataList) => {
  return [
    {
      typeKey: 'General',
      val: data.general,
    },
    {
      typeKey: 'Identification',
      val: data.identification,
    },
    {
      typeKey: 'Nisa',
      val: data.nisa,
    },
    {
      typeKey: 'DefinedContributionPension',
      val: data.definedContributionPension,
    },
    {
      typeKey: 'Other',
      val: data.other,
    },
  ] as rawDataList[];
};

export type AccountPositionItemsProps = {
  brokerID: string;
  account: string;
  symbol: string;
  symbolName: string;
  accountType: number;
  productType: number;
  cashMargin: number;
  side: number;
  qty: number | null;
  price: number | null;
  currentAmount: number | null;
  currentAmountUSD: number | null;
  profitOrLoss: number | null;
  profitOrLossUSD: number | null;
  currencyUnit: string | null;
};

type AccountDataProps = {
  TotalAsset: {
    general: number | null;
    identification: number | null;
    nisa: number | null;
    definedContributionPension: number | null;
    other: number | null;
    positions: AccountPositionItemsProps[];
  };
  GroupAssets: {
    ID: string;
    name: string;
    general: number | null;
    identification: number | null;
    nisa: number | null;
    definedContributionPension: number | null;
    other: number | null;
    positions: AccountPositionItemsProps[];
  }[];
};

export const PortfolioRatioStuffingAccount = ({
  asset,
  brokersInfo,
  UserBrokerUpdateTime,
  assetBundleType,
  toPreview,
  backHome,
  selectedType,
  setSelectedType,
  getIsOutOfDate,
}: PortfolioRatioStuffingProps &
  PortfolioRatioStuffingTabProps): React.ReactElement => {
  const { data, isFetching, isRefetching, refetch } = useQuery(
    'portfolioStuffingAccount',
    fetchAccountData,
    {
      refetchOnWindowFocus: false,
    }
  );

  useEffect(() => {
    (async () => {
      await refetch();
    })();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedType]);

  if (data === null || data === undefined || (isFetching && isRefetching))
    return <PortfolioLoading type={'account'} />;

  const accountData_ = data as AccountDataProps;

  const getRatio = getRatio_(asset.TotalAssetAmount ?? 0);
  const getDatum = ({
    typeKey,
    val,
  }: {
    typeKey: TypeKey;
    val: number | null;
  }): {
    typeKey: TypeKey;
    ratio: number | null;
    amount: number | null;
  }[] => {
    if (val === null) return [{ typeKey, amount: null, ratio: null }];
    return [{ typeKey, amount: val, ratio: getRatio(val) }];
  };

  const accountData =
    assetBundleType.key === 0
      ? accountData_.TotalAsset
      : accountData_.GroupAssets.find(
          GroupAsset => GroupAsset.ID === assetBundleType.key.toString()
        );

  if (accountData === undefined) return <PortfolioLoading type={'account'} />;

  const list = getAccountRowDataList(accountData);
  const rawData = list.flatMap(item => getDatum(item));

  const rawDataPositions = accountData.positions
    .filter(position => !isHiddenProductTypes(position.productType))
    .map(position => {
      const isOutOfDate = getIsOutOfDate(position)
        ? { isOutOfDate: true, account: position.account }
        : null;

      return { ...position, refresh: isOutOfDate };
    });

  return (
    <PortfolioRatioStuffingInternal
      {...{
        asset,
        brokersInfo,
        UserBrokerUpdateTime,
        assetBundleType,
        toPreview,
        backHome,
        selectedType,
        setSelectedType,
        rawData,
        rawDataPositions,
        isFetching,
        isRefetching,
      }}
    />
  );
};

const fetchAccountData = async () => {
  const res_ = await api.getAccountClassification();
  return convertToAccount(res_);
};
